import { Box } from '@mui/material'
import { Button } from '../../interaxDesignSystemComponents'
import { INTERAX_COLORS } from '../../constants/colors'
import { TrainingSprint, TrainingSprintStatus } from '../../types/TrainingSprintTypes'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { SprintTeamMembersCollapsible } from './SprintTeamMembersCollapsible'

interface TrainingSprintCardProps {
  sprint: TrainingSprint
  onContinue?: (sprint: TrainingSprint) => void
  onViewDetails?: (sprint: TrainingSprint) => void
}

export default function TrainingSprintCard({ sprint, onContinue, onViewDetails }: TrainingSprintCardProps) {
  const { t } = useTranslation()

  const getStatusColor = (status: TrainingSprintStatus): string => {
    switch (status) {
      case TrainingSprintStatus.INACTIVE:
        return INTERAX_COLORS.sun // Orange for draft/inactive
      case TrainingSprintStatus.ACTIVE:
        return INTERAX_COLORS.green // Green for active
      case TrainingSprintStatus.COMPLETED:
        return INTERAX_COLORS.thunderGrey // Grey for completed
      default:
        return INTERAX_COLORS.thunderGrey
    }
  }

  const getButtonText = (status: TrainingSprintStatus): string => {
    switch (status) {
      case TrainingSprintStatus.INACTIVE:
        return t('facilitator.trainingSprintManagement.card.continue')
      case TrainingSprintStatus.ACTIVE:
      case TrainingSprintStatus.COMPLETED:
        return t('facilitator.trainingSprintManagement.card.viewDetails')
      default:
        return t('facilitator.trainingSprintManagement.card.viewDetails')
    }
  }

  const getButtonVariant = (status: TrainingSprintStatus): 'primary' | 'secondary' => {
    return status === TrainingSprintStatus.INACTIVE ? 'primary' : 'secondary'
  }

  return (
    <Box
      sx={{
        backgroundColor: INTERAX_COLORS.heritageBlue40,
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        border: `1px solid ${INTERAX_COLORS.thunderGrey}`
      }}
    >
      {/* Status Tag */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Box
          sx={{
            backgroundColor: getStatusColor(sprint.status),
            color: INTERAX_COLORS.white,
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            textTransform: 'capitalize'
          }}
        >
          {sprint.status}
        </Box>
      </Box>

      {/* Sprint Title */}
      <Box className="text-headline-5" sx={{ color: INTERAX_COLORS.white }} data-testid="sprint-name-item">
        {sprint.title}
      </Box>

      {/* Sprint Details */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('facilitator.trainingSprintManagement.card.duration', { duration: sprint.duration })}
        </Box>
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('facilitator.trainingSprintManagement.card.sessions', { count: sprint.sessions })}
        </Box>
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('facilitator.trainingSprintManagement.card.teams', { count: sprint.teams })}
        </Box>
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('facilitator.trainingSprintManagement.card.participants', { count: sprint.participants })}
        </Box>
        <Box className="text-body-3" sx={{ color: INTERAX_COLORS.thunderGrey }}>
          {t('facilitator.trainingSprintManagement.card.progress', { percent: sprint.progress })}
        </Box>
      </Box>

      {/* Progress Bar (only for active/completed) */}
      {(sprint.status === TrainingSprintStatus.ACTIVE || sprint.status === TrainingSprintStatus.COMPLETED) && (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              width: '100%',
              height: '4px',
              backgroundColor: INTERAX_COLORS.thunderGrey,
              borderRadius: '2px',
              overflow: 'hidden'
            }}
          >
            <Box
              sx={{
                width: `${sprint.progress}%`,
                height: '100%',
                backgroundColor:
                  sprint.status === TrainingSprintStatus.COMPLETED ? INTERAX_COLORS.green : INTERAX_COLORS.green,
                transition: 'width 0.3s ease'
              }}
            />
          </Box>
        </Box>
      )}

      {/* Teams & Participants */}
      {sprint.teamDetails?.length || sprint.unassignedMemberNames?.length ? (
        <SprintTeamMembersCollapsible
          teamDetails={sprint.teamDetails ?? []}
          unassignedMemberNames={sprint.unassignedMemberNames}
        />
      ) : null}

      {/* Action Button */}
      <Box sx={{ width: '100%', marginTop: '8px' }}>
        <Button
          variant={getButtonVariant(sprint.status)}
          fullWidth
          onClick={() => {
            if (sprint.status === TrainingSprintStatus.INACTIVE && onContinue) {
              onContinue(sprint)
            } else if (onViewDetails) {
              onViewDetails(sprint)
            }
          }}
        >
          {getButtonText(sprint.status)}
        </Button>
      </Box>
    </Box>
  )
}
